import React, {useEffect, useRef, useState} from 'react'
import {Box, Flex, Text, useOutsideClick} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import {UrlQuery} from '_app/types'
import {CategoryTree} from '_app/types/api'
import Badge from '_app/components/Badge'
import {useRouter} from '_app/hooks/useRouter'
import useBodyScroll from '_app/hooks/useBodyScroll'
import SettingsIcon from '_app/components/Icons/SettingsIcon'
import MobileDropdownButton from './MobileDropdownButton'
import {useFilters} from './Filters/useFilters'
import CategoriesMobile from './CategoriesMobile'
import MobileFiltersForm from './Filters/MobileFiltersForm'
import {StyledListingBox} from './MobileNavigation.styled'
import ElementsSizesEnum from '../../enums/ElementsSizesEnum/ElementsSizesEnum'

enum MobileDropdown {
    CATEGORIES,
    FILTERS,
    SORT,
}

interface MobileNavigationProps {
    categoryUid: string;
    activeFilters?: UrlQuery;
    order: string;
    category: CategoryTree;
    navigation: any;
}

const MobileNavigation = ({
        categoryUid,
        activeFilters = {},
        order,
        category,
        navigation,
    }: MobileNavigationProps) => {
    const {t} = useTranslation('listing')
    const ref = useRef<HTMLDivElement | null>(null)
    const [activeDropdown, setActiveDropdown] = useState<MobileDropdown | undefined>()
    const filtersHook = useFilters(categoryUid, activeFilters)
    const {lock: lockBodyScroll, unlock: unlockBodyScroll} = useBodyScroll()
    useOutsideClick({ ref: ref, handler: () => setActiveDropdown(undefined) })
    const {pathname, query} = useRouter()
    const onOpen = (dropdown: MobileDropdown) => {
        setActiveDropdown(dropdown)
        lockBodyScroll()
    }
    const onClose = () => {
        setActiveDropdown(undefined)
        unlockBodyScroll()
    }
    const onToggle = (dropdown: MobileDropdown) => {
        if (dropdown === activeDropdown) onClose()
        else onOpen(dropdown)
    }
    useEffect(() => {
        onClose()
        return () => onClose()
    }, [pathname, query])

    return (
        <Box
            position="sticky"
            top={ElementsSizesEnum.NAVBAR_HEIGHT.base}
            zIndex={2}
            ref={ref}
            overflow="hidden"
        >
            <Flex
                display={{
                    base: 'flex',
                    lg: 'none',
                }}
                bg="white"
                borderColor="mercury"
                borderBottomWidth="1px"
                flexDirection="column"
            >
                <CategoriesMobile
                    {...{
                        navigation,
                        category,
                    }}
                />
                <Box
                    position={
                        activeDropdown === MobileDropdown.CATEGORIES ? 'fixed' : 'relative'
                    }
                    bottom={0}
                    width="100%"
                >
                    <MobileDropdownButton
                        isOpen={activeDropdown === MobileDropdown.FILTERS}
                        onToggle={() => onToggle(MobileDropdown.FILTERS)}
                        text={
                            <Badge
                                value={
                                    activeDropdown !== MobileDropdown.FILTERS
                                        ? filtersHook.activeFiltersCount
                                        : 0
                                }
                                right={-2}
                                inline
                            >
                                <Box marginRight={3}>
                                    <SettingsIcon/>
                                </Box>
                                <Text fontWeight={300}>{t('FILTER_AND_SORT')}</Text>
                            </Badge>
                        }
                        withBorder
                    />
                </Box>
                {activeDropdown === MobileDropdown.FILTERS && (
                    <StyledListingBox
                        top={`calc(${ElementsSizesEnum.NAVBAR_HEIGHT.base} + 6rem)`}
                        borderWidth={0}
                    >
                        <MobileFiltersForm
                            active={order}
                            {...{
                                ...filtersHook,
                                onClose,
                            }}
                        />
                    </StyledListingBox>
                )}
            </Flex>
        </Box>
    )
}

export default MobileNavigation
